import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Plus, Search, Building, ExternalLink, Edit2, Trash2, X, Sparkles, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import CsvImport from '../components/app/CsvImport';

const empty = {
  municipality: '', province: '', type: 'urbano',
  max_height_m: 6, min_distance_residential_m: 100, retranqueo_m: 3,
  extra_rules: '',
  requires_license: true, source_url: '', notes: '',
  verified: false,
};

const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-xl font-black text-white">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const Normativa = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState(null);
  const [verifyingIds, setVerifyingIds] = useState({}); // id -> true
  const [bulkVerifying, setBulkVerifying] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    const params = {};
    if (search) params.municipality = search;
    if (typeFilter) params.type = typeFilter;
    const [r, s] = await Promise.all([
      api.get('/normativa', { params }),
      api.get('/normativa/verification/status').catch(() => ({ data: null })),
    ]);
    setItems(r.data);
    if (s.data) setStatus(s.data);
  };
  useEffect(() => { load(); }, [search, typeFilter]);

  const verifyOne = async (n) => {
    setVerifyingIds((v) => ({ ...v, [n.id]: true }));
    setToast(null);
    try {
      const { data } = await api.post(`/normativa/${n.id}/verify-ai`);
      const msg = data.changes?.length
        ? `✅ ${data.municipality}: ${data.changes.length} cambio(s) detectado(s)`
        : `✓ ${data.municipality}: sin cambios materiales`;
      setToast({ type: 'ok', msg });
      await load();
    } catch (e) {
      setToast({ type: 'err', msg: 'Error Adquiriam: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setVerifyingIds((v) => { const c = { ...v }; delete c[n.id]; return c; });
    }
  };

  const verifyStale = async () => {
    setBulkVerifying(true);
    setToast(null);
    try {
      const { data } = await api.post('/normativa/verify-stale?limit=25');
      setToast({ type: 'ok', msg: `${data.queued} fichas en cola. Adquiriam las está refrescando en segundo plano (~30s/ficha).` });
    } catch (e) {
      setToast({ type: 'err', msg: 'Error: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setBulkVerifying(false);
    }
  };

  const save = async () => {
    const payload = { ...form,
      max_height_m: form.max_height_m === '' || form.max_height_m == null ? null : parseFloat(form.max_height_m),
      min_distance_residential_m: form.min_distance_residential_m === '' || form.min_distance_residential_m == null ? null : parseFloat(form.min_distance_residential_m),
      retranqueo_m: form.retranqueo_m === '' || form.retranqueo_m == null ? null : parseFloat(form.retranqueo_m),
    };
    if (editing) await api.put(`/normativa/${editing}`, payload);
    else await api.post('/normativa', payload);
    setModal(false); setEditing(null); setForm(empty); load();
  };

  const edit = (n) => {
    setEditing(n.id);
    setForm({ ...n });
    setModal(true);
  };

  const remove = async (id) => {
    if (!confirm('¿Eliminar normativa?')) return;
    await api.delete(`/normativa/${id}`);
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">NORMATIVA</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Reglamentación por municipio</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Alturas máximas, distancias y licencias para antenas urbanas y rurales.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport entity="normativa" onImported={load}/>
          <button onClick={verifyStale} disabled={bulkVerifying} data-testid="btn-verify-stale"
            className="inline-flex items-center gap-2 border border-[#A3FF12]/40 text-[#A3FF12] hover:bg-[#A3FF12]/10 px-4 py-2.5 rounded-full font-bold text-sm disabled:opacity-50">
            {bulkVerifying ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
            Verificar pendientes (Adquiriam)
          </button>
          <button onClick={() => { setEditing(null); setForm(empty); setModal(true); }}
            className="inline-flex items-center gap-2 bg-[#A3FF12] text-black px-5 py-2.5 rounded-full font-bold hover:bg-[#b8ff3a] transition-colors text-sm">
            <Plus size={16}/> Añadir
          </button>
        </div>
      </div>

      {/* Verification KPI banner */}
      {status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="verification-status">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-white/40">Total fichas</div>
            <div className="text-2xl font-black text-white">{status.total}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-[#A3FF12]">Verificadas (&lt;12m)</div>
            <div className="text-2xl font-black text-[#A3FF12]">{status.verified_recent + status.never_verified}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-orange-500/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-orange-300">Obsoletas (&gt;12m)</div>
            <div className="text-2xl font-black text-orange-400">{status.stale_over_12m}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-yellow-500/30 rounded-xl px-3 py-2">
            <div className="text-[10px] uppercase text-yellow-300">Pendientes Adquiriam</div>
            <div className="text-2xl font-black text-yellow-400">{status.pending_ai}</div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm border ${toast.type === 'ok' ? 'bg-[#A3FF12]/10 border-[#A3FF12]/30 text-[#A3FF12]' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar municipio..."
            className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-[#0a0a0a] border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]">
          <option value="">Todos los tipos</option>
          <option value="urbano">Urbano</option>
          <option value="rural">Rural</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((n) => (
          <div key={n.id} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 hover:border-[#A3FF12]/30 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Building className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-black">{n.municipality}</h3>
                {n.verified && !n.needs_verification && <span className="text-[9px] font-black tracking-widest bg-[#A3FF12]/20 text-[#A3FF12] px-1.5 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 size={10}/>VERIF</span>}
                {n.needs_verification && <span className="text-[9px] font-black tracking-widest bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded-full flex items-center gap-1"><AlertCircle size={10}/>SIN ADQUIRIAM</span>}
                {n.last_verified_at && !n.needs_verification && (
                  <span className="text-[9px] text-white/40" title={`Última verificación Adquiriam: ${new Date(n.last_verified_at).toLocaleString('es-ES')}`}>
                    Adquiriam {new Date(n.last_verified_at).toLocaleDateString('es-ES', { month:'short', year:'2-digit' })}
                  </span>
                )}
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full border uppercase ${n.type === 'urbano' ? 'border-[#A3FF12]/30 text-[#A3FF12]' : 'border-orange-500/30 text-orange-400'}`}>{n.type}</span>
            </div>
            {n.province && <div className="text-white/40 text-xs mb-3">{n.province}</div>}
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-white/50">Altura máx</span><span className="text-white font-semibold">{n.max_height_m ? `${n.max_height_m} m` : 'N/D'}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Dist. residencial</span><span className="text-white font-semibold">{n.min_distance_residential_m != null ? `${n.min_distance_residential_m} m` : 'N/D'}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Retranqueo</span><span className="text-white font-semibold">{n.retranqueo_m != null ? `${n.retranqueo_m} m` : 'N/D'}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Licencia</span><span className="text-white font-semibold">{n.requires_license ? 'Sí' : 'No'}</span></div>
            </div>
            {n.extra_rules && (
              <div className="mt-3 bg-orange-500/10 border border-orange-500/20 text-orange-200 text-xs rounded-lg px-3 py-2 leading-relaxed">
                <strong>⚠ Reglas extra:</strong> {n.extra_rules}
              </div>
            )}
            {n.license_regime && (
              <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                <span className="text-white/50">Régimen</span>
                <span className={`font-black uppercase px-2 py-1 rounded-full border ${
                  n.license_regime === 'licencia' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                  n.license_regime === 'declaracion_responsable' ? 'bg-[#A3FF12]/10 text-[#A3FF12] border-[#A3FF12]/30' :
                  n.license_regime === 'comunicacion_previa' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                  'bg-orange-500/10 text-orange-400 border-orange-500/30'
                }`}>
                  {n.license_regime === 'declaracion_responsable' ? 'DR' : n.license_regime === 'comunicacion_previa' ? 'COM. PREVIA' : n.license_regime}
                </span>
              </div>
            )}
            {n.license_conditions && (
              <div className="mt-2 bg-blue-500/5 border border-blue-500/20 text-blue-100 text-xs rounded-lg px-3 py-2 leading-relaxed">
                <strong className="text-blue-300">📋 Condiciones:</strong> {n.license_conditions}
              </div>
            )}
            {n.protected_zones && (
              <div className="mt-2 bg-purple-500/10 border border-purple-500/20 text-purple-200 text-xs rounded-lg px-3 py-2 leading-relaxed">
                <strong>🏛 Zonas protegidas:</strong> {n.protected_zones}
              </div>
            )}
            {(n.pgou_reference || n.state_framework || n.autonomic_framework) && (
              <div className="mt-2 pt-2 border-t border-white/5 text-xs space-y-1">
                {n.pgou_reference && (
                  <div className="text-white/70">
                    <strong className="text-[#A3FF12]">PGOU:</strong> {n.pgou_reference}
                    {n.pgou_url && <a href={n.pgou_url} target="_blank" rel="noreferrer" className="ml-1 text-[#A3FF12]"><ExternalLink size={10} className="inline"/></a>}
                  </div>
                )}
                {n.state_framework && <div className="text-white/60"><strong className="text-white/80">Estatal:</strong> {n.state_framework}</div>}
                {n.autonomic_framework && <div className="text-white/60"><strong className="text-white/80">Autonómico:</strong> {n.autonomic_framework}</div>}
              </div>
            )}
            {n.notes && <p className="text-white/60 text-xs mt-3 pt-3 border-t border-white/10">{n.notes}</p>}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
              {n.source_url ? (
                <a href={n.source_url} target="_blank" rel="noreferrer" className="text-[#A3FF12] text-xs font-bold inline-flex items-center gap-1 hover:underline">
                  Fuente <ExternalLink size={12}/>
                </a>
              ) : <span/>}
              <div className="flex gap-2 items-center">
                <button onClick={() => verifyOne(n)} disabled={!!verifyingIds[n.id]} data-testid={`btn-verify-${n.id}`}
                  className="text-[#A3FF12]/70 hover:text-[#A3FF12] flex items-center gap-1 text-[11px] font-bold disabled:opacity-50" title="Verificar con Adquiriam">
                  {verifyingIds[n.id] ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                  Adquiriam
                </button>
                <button onClick={() => edit(n)} className="text-white/50 hover:text-white"><Edit2 size={14}/></button>
                <button onClick={() => remove(n.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
          <p className="text-white/50">No hay normativa registrada para esa búsqueda.</p>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar normativa' : 'Nueva normativa'}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/80 text-sm font-semibold">Municipio *</label>
              <input required value={form.municipality} onChange={(e) => setForm({...form, municipality: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Provincia</label>
              <input value={form.province} onChange={(e) => setForm({...form, province: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Tipo</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {['urbano', 'rural'].map((t) => (
                <button key={t} onClick={() => setForm({...form, type: t})}
                  className={`py-3 rounded-xl font-bold text-sm border transition-colors capitalize ${form.type === t ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-white/80 text-sm font-semibold">Altura máx (m)</label>
              <input type="number" step="0.1" value={form.max_height_m || ''} onChange={(e) => setForm({...form, max_height_m: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Dist. resid. (m)</label>
              <input type="number" step="1" value={form.min_distance_residential_m || ''} onChange={(e) => setForm({...form, min_distance_residential_m: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Retranqueo (m)</label>
              <input type="number" step="0.1" value={form.retranqueo_m || ''} onChange={(e) => setForm({...form, retranqueo_m: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Reglas extra (no numéricas)</label>
            <textarea value={form.extra_rules} onChange={(e) => setForm({...form, extra_rules: e.target.value})} rows={2}
              placeholder="Ej: Retranqueo en cono 45° desde alero. BIC prohibido. Plan Especial obligatorio."
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            <p className="text-white/40 text-xs mt-1">Para reglas complejas que no son simples números (conos, ángulos, BIC, etc.).</p>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">URL fuente normativa</label>
            <input value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})} placeholder="https://..."
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            <p className="text-white/40 text-xs mt-1">Búscala en la sede electrónica del ayuntamiento o BOE.</p>
          </div>
          <div>
            <label className="text-white/80 text-sm font-semibold">Notas / detalles</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3}
              className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
              <input type="checkbox" checked={form.requires_license} onChange={(e) => setForm({...form, requires_license: e.target.checked})}
                className="accent-[#A3FF12] w-4 h-4"/>
              Requiere licencia municipal
            </label>
            <label className="flex items-center gap-2 text-[#A3FF12] text-sm cursor-pointer font-semibold">
              <input type="checkbox" checked={form.verified} onChange={(e) => setForm({...form, verified: e.target.checked})}
                className="accent-[#A3FF12] w-4 h-4"/>
              Verificada manualmente
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="px-5 py-2.5 text-white/70 hover:text-white">Cancelar</button>
            <button onClick={save} className="bg-[#A3FF12] text-black px-6 py-2.5 rounded-xl font-bold hover:bg-[#b8ff3a] transition-colors">{editing ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Normativa;
